import React from 'react';
import { Card, SectionTitle, Spinner } from './UI';
import { useNews } from '../hooks/useData';

const PROVIDER_LABEL = { marketaux: 'Marketaux', finnhub: 'Finnhub' };

function SentBar({ label, count, total, color }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>
        <span>{label}</span><span style={{ color, fontWeight: 600 }}>{count} ({pct}%)</span>
      </div>
      <div style={{ height: 4, background: '#1e2736', borderRadius: 2 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 2, transition: 'width 0.6s' }} />
      </div>
    </div>
  );
}

function scoreColor(s) {
  if (s == null) return '#94a3b8';
  if (s > 0.15) return '#10b981';
  if (s < -0.15) return '#f87171';
  return '#94a3b8';
}

function NewsItem({ item }) {
  const sentType = item.sentiment === 'positivo' ? 'positive' : item.sentiment === 'negativo' ? 'negative' : 'neutral';
  const dot = { positivo: '🟢', negativo: '🔴', neutro: '🟡' }[item.sentiment] || '⚪';

  return (
    <div style={{ padding: '12px 0', borderBottom: '1px solid #1a2030' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {item.short || item.source}
        </span>
        <span style={{ fontSize: 11, color: '#334155' }}>{item.date}</span>
      </div>
      <a href={item.link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
        <div style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.5, marginBottom: 6, cursor: 'pointer' }}
          onMouseEnter={e => e.currentTarget.style.color = '#e2e8f0'}
          onMouseLeave={e => e.currentTarget.style.color = '#cbd5e1'}>
          {item.title}
        </div>
      </a>
      {item.summary && (
        <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.4, marginBottom: 6 }}>
          {item.summary.slice(0, 200)}{item.summary.length > 200 ? '…' : ''}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 11, color: sentType === 'positive' ? '#10b981' : sentType === 'negative' ? '#f87171' : '#94a3b8' }}>
          {dot} {item.sentiment?.charAt(0).toUpperCase() + item.sentiment?.slice(1)}
        </span>
        {item.sentiment_score != null && item.sentiment_score !== 0 && (
          <span style={{ fontSize: 11, color: scoreColor(item.sentiment_score), fontFamily: "'IBM Plex Mono', monospace" }}>
            {item.sentiment_score > 0 ? '+' : ''}{item.sentiment_score}
          </span>
        )}
      </div>
    </div>
  );
}

// Estrae il "detail" dal messaggio di errore dell'API
function parseError(errMsg) {
  if (!errMsg) return null;
  const m = errMsg.match(/\{"detail":"(.+?)"\}/);
  const detail = m ? m[1] : errMsg;
  const isConfig = /API_KEY non configurata|non configurata/i.test(detail);
  const isLimit  = /[Ll]imite richieste/.test(detail);
  return { detail, isConfig, isLimit };
}

function ConfigNotice({ detail, isConfig, isLimit }) {
  return (
    <Card style={{ borderColor: isConfig ? '#2563eb40' : '#f59e0b40' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <span style={{ fontSize: 20 }}>{isConfig ? '🔑' : isLimit ? '⏳' : '⚠'}</span>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0', marginBottom: 6 }}>
            {isConfig ? 'Provider notizie non configurato'
              : isLimit ? 'Limite richieste raggiunto'
              : 'Notizie non disponibili'}
          </div>
          <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.6 }}>
            {isConfig ? (
              <>
                Per ricevere le notizie con sentiment per ticker serve una chiave API gratuita.
                Crea il file <code style={{ background: '#1a2030', padding: '1px 6px', borderRadius: 4, color: '#cbd5e1' }}>.env</code> nella
                cartella del progetto (copia da <code style={{ background: '#1a2030', padding: '1px 6px', borderRadius: 4, color: '#cbd5e1' }}>.env.example</code>),
                inserisci la chiave e riavvia con <code style={{ background: '#1a2030', padding: '1px 6px', borderRadius: 4, color: '#cbd5e1' }}>docker compose up -d</code>.
                <div style={{ marginTop: 10 }}>
                  <a href="https://www.marketaux.com" target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa' }}>marketaux.com</a>
                  {' '}(consigliato, con sentiment) ·{' '}
                  <a href="https://finnhub.io" target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa' }}>finnhub.io</a>
                  {' '}(alternativa)
                </div>
              </>
            ) : isLimit ? (
              <>Hai esaurito le richieste giornaliere del tier gratuito. Riprova più tardi, oppure passa a Finnhub (limite per minuto più alto) impostando <code style={{ background: '#1a2030', padding: '1px 6px', borderRadius: 4, color: '#cbd5e1' }}>NEWS_PROVIDER=finnhub</code> nel file <code style={{ background: '#1a2030', padding: '1px 6px', borderRadius: 4, color: '#cbd5e1' }}>.env</code>.</>
            ) : (
              <>{detail}</>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function NewsPanel({ ticker, market, companyName }) {
  const { data, loading, error } = useNews(ticker, companyName, market);

  if (loading) return <Spinner />;
  if (error) {
    const e = parseError(error);
    return <ConfigNotice {...e} />;
  }

  const summary  = data?.sentiment_summary;
  const items    = data?.items || [];
  const provider = PROVIDER_LABEL[data?.provider] || data?.provider;

  return (
    <div>
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 20 }}>
          {[
            { label: 'Notizie trovate',    value: data.total_news,       sub: `via ${provider}` },
            { label: 'Sentiment globale',  value: summary.global?.toUpperCase() || '—', sub: 'Ultime 3 settimane', color: summary.global === 'positivo' ? '#10b981' : summary.global === 'negativo' ? '#f87171' : '#94a3b8' },
            { label: 'Score sentiment',    value: `${summary.score}/100`, sub: 'Indice composto', color: summary.score >= 60 ? '#10b981' : summary.score >= 40 ? '#fbbf24' : '#f87171' },
          ].map(m => (
            <div key={m.label} style={{ background: '#0f1319', border: '1px solid #1a2030', borderRadius: 8, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{m.label}</div>
              <div style={{ fontSize: 20, fontWeight: 600, color: m.color || '#e2e8f0', fontFamily: "'IBM Plex Mono', monospace" }}>{m.value}</div>
              {m.sub && <div style={{ fontSize: 11, color: '#475569', marginTop: 3 }}>{m.sub}</div>}
            </div>
          ))}
        </div>
      )}

      {summary && data.total_news > 0 && (
        <Card style={{ marginBottom: 20 }}>
          <SectionTitle>Distribuzione sentiment</SectionTitle>
          <SentBar label="Notizie positive" count={summary.positive} total={data.total_news} color="#10b981" />
          <SentBar label="Notizie neutre"   count={summary.neutral}  total={data.total_news} color="#475569" />
          <SentBar label="Notizie negative" count={summary.negative} total={data.total_news} color="#f87171" />
        </Card>
      )}

      <Card>
        <SectionTitle>
          {items.length > 0
            ? `${items.length} notizie per ${ticker}`
            : `Nessuna notizia trovata per ${ticker}`}
        </SectionTitle>
        {items.length === 0 && (
          <div style={{ fontSize: 13, color: '#475569', paddingTop: 8 }}>
            Il provider {provider || ''} non ha restituito articoli recenti per questo titolo.
            Alcune società quotate fuori dagli USA hanno copertura più limitata sul tier gratuito.
          </div>
        )}
        {items.map((item, i) => <NewsItem key={i} item={item} />)}
      </Card>
    </div>
  );
}
